<template>
  <div v-if="isOpen" class="modal-overlay" @click.self="closeDialog">
    <div class="modal-content">
      <!-- Header -->
      <div class="modal-header">
        <h2 class="modal-title">{{ $t('controlCenter.userManagement.createAuditorTitle') }}</h2>
        <button class="close-button" @click="closeDialog">&times;</button>
      </div>

      <!-- Body -->
      <div class="modal-body">
        <form @submit.prevent="handleSubmit">
          <!-- Basic Information Section -->
          <div class="form-section">
            <h3 class="section-title">{{ $t('controlCenter.userManagement.basicInfo') }}</h3>
            
            <div class="form-row">
              <div class="form-group">
                <label for="firstName">{{ $t('controlCenter.userManagement.firstName') }} <span class="required">*</span></label>
                <input 
                  type="text" 
                  id="firstName" 
                  v-model="form.fullName.firstName" 
                  class="form-control" 
                  required
                  :placeholder="$t('controlCenter.userManagement.firstNamePlaceholder')"
                >
              </div>
              <div class="form-group">
                <label for="lastName">{{ $t('controlCenter.userManagement.lastName') }} <span class="required">*</span></label>
                <input 
                  type="text" 
                  id="lastName" 
                  v-model="form.fullName.lastName" 
                  class="form-control" 
                  required
                  :placeholder="$t('controlCenter.userManagement.lastNamePlaceholder')"
                >
              </div>
            </div>

            <div class="form-group">
              <label for="email">{{ $t('controlCenter.userManagement.email') }} <span class="required">*</span></label>
              <input 
                type="email" 
                id="email" 
                v-model="form.email" 
                class="form-control" 
                required
                :placeholder="$t('controlCenter.userManagement.emailPlaceholder')"
              >
            </div>

            <div class="form-group">
              <label for="password">{{ $t('controlCenter.userManagement.password') }} <span class="required">*</span></label>
              <div class="password-input-wrapper">
                <input 
                  :type="showPassword ? 'text' : 'password'" 
                  id="password" 
                  v-model="form.password" 
                  class="form-control" 
                  required
                  :placeholder="$t('controlCenter.userManagement.passwordPlaceholder')"
                >
                <button 
                  type="button" 
                  class="password-toggle" 
                  @click="showPassword = !showPassword"
                  tabindex="-1"
                >
                  <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                </button>
              </div>
              <small class="password-hint">{{ $t('controlCenter.userManagement.passwordHint') }}</small>
            </div>
          </div>

          <!-- Personal Information Section -->
          <div class="form-section">
            <h3 class="section-title">{{ $t('controlCenter.userManagement.personalInfo') }}</h3>
            
            <div class="form-row">
              <div class="form-group">
                <label for="sex">{{ $t('controlCenter.userManagement.sex') }}</label>
                <select v-model="form.sex" id="sex" class="form-control">
                  <option value="UNKNOWN">{{ $t('controlCenter.userManagement.sexUnknown') }}</option>
                  <option value="MALE">{{ $t('controlCenter.userManagement.sexMale') }}</option>
                  <option value="FEMALE">{{ $t('controlCenter.userManagement.sexFemale') }}</option>
                </select>
              </div>
              <div class="form-group">
                <label for="birthday">{{ $t('controlCenter.userManagement.birthday') }}</label>
                <input 
                  type="date" 
                  id="birthday" 
                  v-model="form.birthday" 
                  class="form-control"
                >
              </div>
            </div>

            <div class="form-group">
              <label for="phoneNumber">{{ $t('controlCenter.userManagement.phoneNumber') }}</label>
              <input 
                type="tel" 
                id="phoneNumber" 
                v-model="form.phoneNumber" 
                class="form-control"
                :placeholder="$t('controlCenter.userManagement.phoneNumberPlaceholder')"
              >
            </div>

            <div class="form-group">
              <label for="nationality">{{ $t('controlCenter.userManagement.nationality') }}</label>
              <select v-model="form.nationality" id="nationality" class="form-control">
                <option value="">{{ $t('controlCenter.userManagement.selectNationality') }}</option>
                <option v-for="nation in nationalities" :key="nation" :value="nation">
                  {{ nation }}
                </option>
              </select>
            </div>
          </div>

          <!-- Address Information Section -->
          <div class="form-section">
            <h3 class="section-title">{{ $t('controlCenter.userManagement.addressInfo') }}</h3>
            
            <div class="form-group">
              <label for="country">{{ $t('controlCenter.userManagement.country') }}</label>
              <select v-model="form.address.countryOrRegion" id="country" class="form-control">
                <option value="">{{ $t('controlCenter.userManagement.selectCountry') }}</option>
                <option v-for="country in countries" :key="country" :value="country">
                  {{ country }}
                </option>
              </select>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label for="state">{{ $t('controlCenter.userManagement.stateOrProvince') }}</label>
                <input 
                  type="text" 
                  id="state" 
                  v-model="form.address.stateOrProvince" 
                  class="form-control"
                  :placeholder="$t('controlCenter.userManagement.statePlaceholder')"
                >
              </div>
              <div class="form-group">
                <label for="city">{{ $t('controlCenter.userManagement.city') }}</label>
                <input 
                  type="text" 
                  id="city" 
                  v-model="form.address.city" 
                  class="form-control"
                  :placeholder="$t('controlCenter.userManagement.cityPlaceholder')"
                >
              </div>
            </div>

            <div class="form-group">
              <label for="postal">{{ $t('controlCenter.userManagement.postalCode') }}</label>
              <input 
                type="text" 
                id="postal" 
                v-model="form.address.postalCode" 
                class="form-control"
                :placeholder="$t('controlCenter.userManagement.postalCodePlaceholder')"
              >
            </div>

            <div class="form-group">
              <label for="address1">{{ $t('controlCenter.userManagement.addressLine1') }}</label>
              <input 
                type="text" 
                id="address1" 
                v-model="form.address.addressLine1" 
                class="form-control"
                :placeholder="$t('controlCenter.userManagement.addressLine1Placeholder')"
              >
            </div>

            <div class="form-group">
              <label for="address2">{{ $t('controlCenter.userManagement.addressLine2') }}</label>
              <input 
                type="text" 
                id="address2" 
                v-model="form.address.addressLine2" 
                class="form-control"
                :placeholder="$t('controlCenter.userManagement.addressLine2Placeholder')"
              >
            </div>
          </div>

          <!-- Status Section -->
          <div class="form-section">
            <h3 class="section-title">{{ $t('controlCenter.userManagement.status') }}</h3>
            
            <div class="checkbox-group">
              <label class="checkbox-label">
                <input 
                  type="checkbox" 
                  v-model="form.enabled" 
                  class="checkbox-input"
                >
                <span>{{ $t('controlCenter.userManagement.enableUser') }}</span>
              </label>
            </div>
          </div>
        </form>
      </div>

      <!-- Footer -->
      <div class="modal-footer">
        <button class="btn btn-secondary" @click="closeDialog" :disabled="loading">
          {{ $t('controlCenter.userManagement.cancel') }}
        </button>
        <button class="btn btn-primary" @click="handleSubmit" :disabled="loading">
          <span v-if="loading">
            <i class="fas fa-spinner fa-spin"></i> {{ $t('controlCenter.userManagement.creating') }}
          </span>
          <span v-else>
            <i class="fas fa-plus"></i> {{ $t('controlCenter.userManagement.createAuditor') }}
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { userApi } from '@/api/user';

export default {
  name: 'CreateAuditorDialog',
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    createAuditor: {
      type: Function,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      showPassword: false,
      form: {
        email: '',
        password: '',
        fullName: {
          firstName: '',
          lastName: ''
        },
        sex: 'UNKNOWN',
        birthday: '',
        phoneNumber: '',
        address: {
          countryOrRegion: '',
          stateOrProvince: '',
          city: '',
          postalCode: '',
          addressLine1: '',
          addressLine2: ''
        },
        nationality: '',
        enabled: true
      },
      nationalities: [
        'AFGHAN', 'ALBANIAN', 'ALGERIAN', 'AMERICAN', 'ANDORRAN', 'ANGOLAN',
        'ANTIGUA_AND_BARBUDAN', 'ARGENTINE', 'ARMENIAN', 'AUSTRALIAN', 'AUSTRIAN',
        'AZERBAIJANI', 'BAHAMIAN', 'BAHRAINI', 'BANGLADESHI', 'BARBADIAN',
        'BELARUSIAN', 'BELGIAN', 'BELIZEAN', 'BENINESE', 'BHUTANESE', 'BOLIVIAN',
        'BOSNIAN_AND_HERZEGOVINIAN', 'BRAZILIAN', 'BRITISH', 'BRUNEIAN', 'BULGARIAN',
        'BURKINABE', 'BURMESE_OR_MYANMAR', 'BURUNDIAN', 'CAMBODIAN', 'CAMEROONIAN',
        'CANADIAN', 'CAPE_VERDEAN', 'CENTRAL_AFRICAN', 'CHADIAN', 'CHILEAN',
        'CHINESE', 'COLOMBIAN', 'COMORIAN', 'CONGOLESE_DEMOCRATIC_REPUBLIC_OF_CONGO',
        'CONGOLESE_REPUBLIC_OF_CONGO', 'COSTA_RICAN', 'CROATIAN', 'CUBAN', 'CYPRIOT',
        'CZECH', 'DANISH', 'DJIBOUTIAN', 'DOMINICAN_DOMINICA', 'DOMINICAN_DOMINICAN_REPUBLIC',
        'DUTCH', 'ECUADORIAN', 'EGYPTIAN', 'EMIRATI', 'EQUATORIAL_GUINEAN', 'ERITREAN',
        'ESTONIAN', 'ETHIOPIAN', 'FIJIAN', 'FILIPINO', 'FINNISH', 'FRENCH', 'GABONESE',
        'GAMBIAN', 'GEORGIAN', 'GERMAN', 'GHANAIAN', 'GREEK', 'GRENADIAN', 'GUATEMALAN',
        'GUINEA_BISSAUAN', 'GUINEAN', 'GUYANESE', 'HAITIAN', 'HONDURAN', 'HUNGARIAN',
        'I_KIRIBATI', 'ICELANDIC', 'INDIAN', 'INDONESIAN', 'IRANIAN', 'IRAQI', 'IRISH',
        'ISRAELI', 'ITALIAN', 'IVORIAN', 'JAMAICAN', 'JAPANESE', 'JORDANIAN', 'KAZAKHSTANI',
        'KENYAN', 'KITTITIAN_AND_NEVISIAN', 'KUWAITI', 'KYRGYZSTANI', 'LAO', 'LATVIAN',
        'LEBANESE', 'LIBERIAN', 'LIBYAN', 'LIECHTENSTEINER', 'LITHUANIAN', 'LUXEMBOURGER',
        'MACEDONIAN', 'MALAGASY', 'MALAWIAN', 'MALAYSIAN', 'MALDIVIAN', 'MALIAN', 'MALTESE',
        'MARSHALLESE', 'MAURITANIAN', 'MAURITIAN', 'MEXICAN', 'MICRONESIAN', 'MOLDOVAN',
        'MONEGASQUE', 'MONGOLIAN', 'MONTENEGRIN', 'MOROCCAN', 'MOSOTHO', 'MOTSWANA',
        'MOZAMBICAN', 'NAMIBIAN', 'NAURUAN', 'NEPALESE', 'NEW_ZEALANDER', 'NI_VANUATU',
        'NICARAGUAN', 'NIGERIAN', 'NIGERIEN', 'NORTH_KOREAN', 'NORWEGIAN', 'OMANI',
        'PAKISTANI', 'PALAUAN', 'PANAMANIAN', 'PAPUA_NEW_GUINEAN', 'PARAGUAYAN', 'PERUVIAN',
        'POLISH', 'PORTUGUESE', 'QATARI', 'ROMANIAN', 'RUSSIAN', 'RWANDAN', 'SAINT_LUCIAN',
        'SALVADORAN', 'SAMMARINESE', 'SAMOAN', 'SAO_TOMEAN', 'SAUDI_ARABIAN', 'SENEGALESE',
        'SERBIAN', 'SEYCHELLOIS', 'SIERRA_LEONEAN', 'SINGAPOREAN', 'SLOVAK', 'SLOVENIAN',
        'SOLOMON_ISLANDER', 'SOMALI', 'SOUTH_AFRICAN', 'SOUTH_KOREAN', 'SOUTH_SUDANESE',
        'SPANISH', 'SRI_LANKAN', 'SUDANESE', 'SURINAMESE', 'SWAZI', 'SWEDISH', 'SWISS',
        'SYRIAN', 'TAJIK', 'TANZANIAN', 'THAI', 'TIMORESE', 'TOGOLESE', 'TONGAN',
        'TRINIDADIAN_AND_TOBAGONIAN', 'TUNISIAN', 'TURKISH', 'TURKMEN', 'TUVALUAN',
        'UGANDAN', 'UKRAINIAN', 'URUGUAYAN', 'UZBEKISTANI', 'VENEZUELAN', 'VIETNAMESE',
        'VINCENTIAN', 'YEMENI', 'ZAMBIAN', 'ZIMBABWEAN'
      ],
      countries: [
        'AFGHANISTAN', 'ALAND_ISLANDS', 'ALBANIA', 'ALGERIA', 'AMERICAN_SAMOA', 'ANDORRA',
        'ANGOLA', 'ANGUILLA', 'ANTARCTICA', 'ANTIGUA_AND_BARBUDA', 'ARGENTINA', 'ARMENIA',
        'ARUBA', 'AUSTRALIA', 'AUSTRIA', 'AZERBAIJAN', 'BAHAMAS', 'BAHRAIN', 'BANGLADESH',
        'BARBADOS', 'BELARUS', 'BELGIUM', 'BELIZE', 'BENIN', 'BERMUDA', 'BHUTAN', 'BOLIVIA',
        'BOSNIA_AND_HERZEGOVINA', 'BOTSWANA', 'BOUVET_ISLAND', 'BRAZIL',
        'BRITISH_INDIAN_OCEAN_TERRITORY', 'BRITISH_VIRGIN_ISLANDS', 'BRUNEI', 'BULGARIA',
        'BURKINA_FASO', 'BURUNDI', 'CAMBODIA', 'CAMEROON', 'CANADA', 'CAPE_VERDE',
        'CARIBBEAN_NETHERLANDS', 'CAYMAN_ISLANDS', 'CENTRAL_AFRICAN_REPUBLIC', 'CHAD',
        'CHILE', 'CHINA', 'CHRISTMAS_ISLAND', 'COCOS_KEELING_ISLANDS', 'COLOMBIA', 'COMOROS',
        'CONGO_BRAZZAVILLE', 'CONGO_KINSHASA', 'COOK_ISLANDS', 'COSTA_RICA', 'COTE_D_IVOIRE',
        'CROATIA', 'CUBA', 'CURACAO', 'CYPRUS', 'CZECHIA', 'DENMARK', 'DJIBOUTI', 'DOMINICA',
        'DOMINICAN_REPUBLIC', 'ECUADOR', 'EGYPT', 'EL_SALVADOR', 'EQUATORIAL_GUINEA',
        'ERITREA', 'ESTONIA', 'ESWATINI', 'ETHIOPIA', 'FALKLAND_ISLANDS', 'FAROE_ISLANDS',
        'FIJI', 'FINLAND', 'FRANCE', 'FRENCH_GUIANA', 'FRENCH_POLYNESIA',
        'FRENCH_SOUTHERN_TERRITORIES', 'GABON', 'GAMBIA', 'GEORGIA', 'GERMANY', 'GHANA',
        'GIBRALTAR', 'GREECE', 'GREENLAND', 'GRENADA', 'GUADELOUPE', 'GUAM', 'GUATEMALA',
        'GUERNSEY', 'GUINEA', 'GUINEA_BISSAU', 'GUYANA', 'HAITI', 'HEARD_AND_MCDONALD_ISLANDS',
        'HONDURAS', 'HONG_KONG_SAR_CHINA', 'HUNGARY', 'ICELAND', 'INDIA', 'INDONESIA',
        'IRAN', 'IRAQ', 'IRELAND', 'ISLE_OF_MAN', 'ISRAEL', 'ITALY', 'JAMAICA', 'JAPAN',
        'JERSEY', 'JORDAN', 'KAZAKHSTAN', 'KENYA', 'KIRIBATI', 'KUWAIT', 'KYRGYZSTAN',
        'LAOS', 'LATVIA', 'LEBANON', 'LESOTHO', 'LIBERIA', 'LIBYA', 'LIECHTENSTEIN',
        'LITHUANIA', 'LUXEMBOURG', 'MACAO_SAR_CHINA', 'MADAGASCAR', 'MALAWI', 'MALAYSIA',
        'MALDIVES', 'MALI', 'MALTA', 'MARSHALL_ISLANDS', 'MARTINIQUE', 'MAURITANIA',
        'MAURITIUS', 'MAYOTTE', 'MEXICO', 'MICRONESIA', 'MOLDOVA', 'MONACO', 'MONGOLIA',
        'MONTENEGRO', 'MONTSERRAT', 'MOROCCO', 'MOZAMBIQUE', 'MYANMAR', 'NAMIBIA', 'NAURU',
        'NEPAL', 'NETHERLANDS', 'NEW_CALEDONIA', 'NEW_ZEALAND', 'NICARAGUA', 'NIGER',
        'NIGERIA', 'NIUE', 'NORFOLK_ISLAND', 'NORTH_KOREA', 'NORTH_MACEDONIA',
        'NORTHERN_MARIANA_ISLANDS', 'NORWAY', 'OMAN', 'PAKISTAN', 'PALAU',
        'PALESTINIAN_TERRITORIES', 'PANAMA', 'PAPUA_NEW_GUINEA', 'PARAGUAY', 'PERU',
        'PHILIPPINES', 'PITCAIRN_ISLANDS', 'POLAND', 'PORTUGAL', 'PUERTO_RICO', 'QATAR',
        'REUNION', 'ROMANIA', 'RUSSIA', 'RWANDA', 'SAMOA', 'SAN_MARINO', 'SAO_TOME_AND_PRINCIPE',
        'SAUDI_ARABIA', 'SENEGAL', 'SERBIA', 'SEYCHELLES', 'SIERRA_LEONE', 'SINGAPORE',
        'SINT_MAARTEN', 'SLOVAKIA', 'SLOVENIA', 'SOLOMON_ISLANDS', 'SOMALIA', 'SOUTH_AFRICA',
        'SOUTH_GEORGIA_AND_SOUTH_SANDWICH_ISLANDS', 'SOUTH_KOREA', 'SOUTH_SUDAN', 'SPAIN',
        'SRI_LANKA', 'ST_BARTHELEMY', 'ST_HELENA', 'ST_KITTS_AND_NEVIS', 'ST_LUCIA', 'ST_MARTIN',
        'ST_PIERRE_AND_MIQUELON', 'ST_VINCENT_AND_GRENADINES', 'SUDAN', 'SURINAME',
        'SVALBARD_AND_JAN_MAYEN', 'SWEDEN', 'SWITZERLAND', 'SYRIA', 'TAIWAN_PROVINCE_CHINA',
        'TAJIKISTAN', 'TANZANIA', 'THAILAND', 'TIMOR_LESTE', 'TOGO', 'TOKELAU', 'TONGA',
        'TRINIDAD_AND_TOBAGO', 'TUNISIA', 'TURKEY', 'TURKMENISTAN', 'TURKS_AND_CAICOS_ISLANDS',
        'TUVALU', 'UGANDA', 'UKRAINE', 'UNITED_ARAB_EMIRATES', 'UNITED_KINGDOM', 'UNITED_STATES',
        'URUGUAY', 'US_OUTLYING_ISLANDS', 'US_VIRGIN_ISLANDS', 'UZBEKISTAN', 'VANUATU',
        'VATICAN_CITY', 'VENEZUELA', 'VIETNAM', 'WALLIS_AND_FUTUNA', 'WESTERN_SAHARA', 'YEMEN',
        'ZAMBIA', 'ZIMBABWE'
      ]
    };
  },
  methods: {
    async handleSubmit() {
      this.loading = true;
      try {
        // Prepare the data payload
        const payload = {
          email: this.form.email,
          password: this.form.password,
          fullName: this.form.fullName,
          sex: this.form.sex,
          enabled: this.form.enabled
        };

        // Add optional fields if they have values
        if (this.form.birthday) {
          payload.birthday = this.form.birthday;
        }
        if (this.form.phoneNumber) {
          payload.phoneNumber = this.form.phoneNumber;
        }
        if (this.form.nationality) {
          payload.nationality = this.form.nationality;
        }
        if (this.form.address.countryOrRegion || this.form.address.stateOrProvince || 
            this.form.address.city || this.form.address.postalCode || 
            this.form.address.addressLine1 || this.form.address.addressLine2) {
          payload.address = this.form.address;
        }

        // Call the API
        const response = this.createAuditor
          ? await this.createAuditor(payload)
          : await userApi.createUser(payload);
        
        // Emit success event with the created user data
        this.$emit('auditor-created', response && response.data ? response.data : response);
        
        this.$message.success(this.$t('controlCenter.userManagement.createSuccess'));
        this.closeDialog();
      } catch (error) {
        console.error('Failed to create user:', error);
        const errorMsg = error.response?.data?.message || 
                        this.$t('controlCenter.userManagement.createError');
        this.$message.error(errorMsg);
      } finally {
        this.loading = false;
      }
    },
    closeDialog() {
      // Reset form
      this.form = {
        email: '',
        password: '',
        fullName: {
          firstName: '',
          lastName: ''
        },
        sex: 'UNKNOWN',
        birthday: '',
        phoneNumber: '',
        address: {
          countryOrRegion: '',
          stateOrProvince: '',
          city: '',
          postalCode: '',
          addressLine1: '',
          addressLine2: ''
        },
        nationality: '',
        enabled: true
      };
      this.showPassword = false;
      this.$emit('close');
    }
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  overflow-y: auto;
  padding: 20px 0;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 700px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  animation: modal-fade-in 0.3s ease;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.modal-header {
  padding: 25px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
}

.modal-title {
  margin: 0;
  font-size: 22px;
  font-weight: 600;
  color: #2c3e50;
}

.close-button {
  background: none;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: #7f8c8d;
  transition: color 0.2s;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-button:hover {
  color: #2c3e50;
}

.modal-body {
  padding: 25px;
  overflow-y: auto;
  flex: 1;
}

.modal-footer {
  padding: 20px 25px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  flex-shrink: 0;
}

.form-section {
  margin-bottom: 30px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 15px 0;
  padding-bottom: 10px;
  border-bottom: 2px solid #3498db;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  margin-bottom: 15px;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  font-size: 14px;
  color: #2c3e50;
}

.required {
  color: #e74c3c;
}

.form-control {
  width: 100%;
  padding: 10px 15px;
  border: 1px solid #bdc3c7;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s;
  font-family: inherit;
}

.form-control:focus {
  outline: none;
  border-color: #3498db;
  box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.1);
}

.password-input-wrapper {
  position: relative;
}

.password-toggle {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #7f8c8d;
  font-size: 16px;
  transition: color 0.2s;
}

.password-toggle:hover {
  color: #3498db;
}

.password-hint {
  display: block;
  margin-top: 5px;
  font-size: 12px;
  color: #7f8c8d;
}

.checkbox-group {
  padding: 10px 0;
}

.checkbox-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 14px;
  color: #2c3e50;
}

.checkbox-input {
  width: 18px;
  height: 18px;
  margin-right: 10px;
  cursor: pointer;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background-color: #3498db;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #2980b9;
  box-shadow: 0 4px 12px rgba(52, 152, 219, 0.3);
}

.btn-secondary {
  background-color: #ecf0f1;
  color: #2c3e50;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #bdc3c7;
}

@keyframes modal-fade-in {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 768px) {
  .modal-overlay {
    align-items: flex-start;
    padding: 70px 10px 14px;
  }

  .modal-content {
    width: 95%;
    max-width: none;
    max-height: calc(100dvh - 84px);
    border-radius: 10px;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .modal-body {
    padding: 15px;
  }

  .modal-header {
    padding: 15px;
  }

  .modal-footer {
    padding: 15px;
    flex-direction: column-reverse;
  }

  .modal-title {
    font-size: 18px;
    line-height: 1.35;
    overflow-wrap: anywhere;
  }

  .modal-footer .btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
